/**
 * @fileoverview Demo Section - Interactive product demonstration
 *
 * Purpose: Showcase product interface with hero image
 * Features:
 * - Heading with value proposition
 * - CTA button (See Morgan in action) that opens demo request dialog
 * - Hero dashboard image
 *
 * Used by: Homepage (index.tsx)
 */

import { useState } from "react";
import { Button } from "@/ui/primitives";
import { ArrowRight } from "lucide-react";
import { DemoRequestDialog } from "./-ui.demo-request-dialog";
import { HeroPlatformDiagram } from "./-ui.hero-platform-diagram";

/**
 * Demo section component with animated product showcase
 *
 * Layout: Two-column header (serif heading + accent CTA on the left,
 * right-aligned supporting copy on the right) above a full-width demo container.
 * Features a "See Morgan in action" button that opens the demo request dialog.
 */
export function DemoSection() {
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);

  return (
    <>
      <section className="relative w-full bg-background py-8 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          {/* Header Content - two-column: heading + CTA left, supporting copy right */}
          <div className="grid grid-cols-1 items-start gap-10 md:grid-cols-2 md:gap-16">
            {/* Left: heading */}
            <div>
              <h2 className="max-w-xl text-3xl font-medium leading-[1.1] md:text-[42px]">
                Your AI-native OS <br />
                from pre-appointment <br />
                to finalisation.
              </h2>
            </div>

            {/* Right: supporting copy + CTA, right-aligned */}
            <div className="space-y-3 text-base leading-relaxed text-muted-foreground md:ml-auto md:max-w-sm md:text-right md:text-lg">
              <p className="md:text-lg">
                Eliminate the manual work <br />
                by turning messy matter data into <br />
                <strong className="font-normal">
                  clear actions &amp; insights in minutes
                </strong>
              </p>

              <Button
                variant="default"
                size="lg"
                expandIcon
                className="gap-2 text-base"
                onClick={() => setIsDemoDialogOpen(true)}
              >
                See Morgan in action
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Demo Container */}
          <div className="relative mt-16">
            {/* Demo/Animation Container */}
            <div className="relative overflow-hidden rounded-2xl border border-border bg-muted/30 shadow-2xl">
              {/* Hero Animation Container — taller on mobile so the stacked
                pipeline has room; switches to 16:10 once the desktop diagram
                layout kicks in at 1160px. */}
              <div className="relative h-[46rem] min-[1160px]:aspect-[16/10] min-[1160px]:h-auto">
                {/* Background gradient overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />

                {/* Hero platform diagram animation */}
                <div className="relative flex h-full w-full items-center justify-center p-4 min-[1160px]:p-8">
                  <HeroPlatformDiagram />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DemoRequestDialog
        open={isDemoDialogOpen}
        onOpenChange={setIsDemoDialogOpen}
      />
    </>
  );
}
